import { z } from "zod";

import {
  entityIdSchema,
  hostnameSchema,
  isoInstantSchema,
  localeSchema,
  repoPathSchema,
  slugSchema,
} from "./primitives";

/**
 * Site — the top-level tenant (SPEC §7).
 *
 * MVP ships a single site and the UI hides the site selector, but the data
 * model, the API and every query are site-scoped from day one (SPEC §7,
 * AGENTS.md §19 / Rule 10).
 */

/** GitHub owner / repository naming rules. */
const githubNameSchema = z
  .string()
  .min(1)
  .max(100)
  .regex(/^[A-Za-z0-9][A-Za-z0-9_.-]*$/, "Must be a valid GitHub owner or repository name");

/**
 * GitHub App installation id. Always a decimal string — never a number, so that
 * it survives JSON round-trips without precision loss.
 */
const installationIdSchema = z.string().regex(/^[1-9]\d{0,19}$/, "Must be a decimal installation id");

/**
 * Where content lives inside the content repository.
 *
 * SPEC §55 defaults `content_root` to the empty string, meaning "repository
 * root", so both `""` and a real relative path are valid.
 */
const contentRootSchema = z.union([z.literal(""), repoPathSchema]);

/**
 * Base object shape.
 *
 * Kept separate from the refined schema below because `.refine()` returns a
 * `ZodEffects`, which can no longer be `.pick()`ed — and the update schema needs
 * to derive from the same shape rather than restate it.
 */
const siteObject = z
  .object({
    id: entityIdSchema,
    name: z.string().min(1).max(200),
    slug: slugSchema,
    domain: hostnameSchema,

    githubOwner: githubNameSchema,
    githubRepo: githubNameSchema,
    githubInstallationId: installationIdSchema,

    defaultLocale: localeSchema,
    supportedLocales: z.array(localeSchema).min(1, "A site needs at least one locale"),

    themeId: z.string().min(1).max(100),
    /**
     * Semantic version. SPEC §63 keys a theme by `themeId` + `themeVersion`,
     * so the version has to be resolvable against a published release — a loose
     * string would admit `"v1"` or `"latest"`.
     */
    themeVersion: z
      .string()
      .regex(
        /^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?(?:\+[0-9A-Za-z.-]+)?$/,
        "Must be a semantic version such as 1.0.0",
      ),

    contentRoot: contentRootSchema,

    createdAt: isoInstantSchema,
    updatedAt: isoInstantSchema,
  })
  .strict();

export const siteSchema = siteObject
  .refine((site) => site.supportedLocales.includes(site.defaultLocale), {
    message: "defaultLocale must be one of supportedLocales",
    path: ["defaultLocale"],
  })
  .refine((site) => new Set(site.supportedLocales).size === site.supportedLocales.length, {
    message: "supportedLocales must not contain duplicates",
    path: ["supportedLocales"],
  });

export type Site = z.infer<typeof siteSchema>;

/** Editable subset of a site. Immutable identity fields are excluded (TASK-303). */
export const siteUpdateSchema = siteObject
  .pick({
    name: true,
    domain: true,
    defaultLocale: true,
    supportedLocales: true,
    themeId: true,
    themeVersion: true,
    contentRoot: true,
  })
  .partial()
  .refine((patch) => Object.keys(patch).length > 0, {
    message: "At least one field must be provided",
  });

export type SiteUpdate = z.infer<typeof siteUpdateSchema>;

/**
 * Per-site locale enablement (SPEC §55 `locales`).
 *
 * `supportedLocales` on the site is the source of truth; this row exists so the
 * database can answer "which locales are live" without reading site JSON.
 */
export const localeRecordSchema = z
  .object({
    siteId: entityIdSchema,
    locale: localeSchema,
    enabled: z.boolean(),
  })
  .strict();

export type LocaleRecord = z.infer<typeof localeRecordSchema>;

/**
 * A site's enabled locales, resolved and validated.
 *
 * `defaultLocale` is guaranteed to be present and enabled, and the list is
 * deduplicated — callers can iterate without re-checking.
 */
export interface ResolvedLocales {
  readonly defaultLocale: string;
  readonly enabled: readonly string[];
  readonly all: readonly string[];
}

export function resolveLocales(
  site: Pick<Site, "defaultLocale" | "supportedLocales">,
  records: readonly LocaleRecord[],
): ResolvedLocales {
  const declared = new Set(site.supportedLocales);
  const disabled = new Set(
    records.filter((record) => !record.enabled && declared.has(record.locale)).map((r) => r.locale),
  );

  const enabled = site.supportedLocales.filter((locale) => !disabled.has(locale));

  // A site with no enabled locale cannot render anything; falling back to the
  // default is safer than producing a blog with zero pages (SPEC §11 forbids
  // silent *content* fallback, not locale enablement).
  if (enabled.length === 0) {
    return {
      defaultLocale: site.defaultLocale,
      enabled: [site.defaultLocale],
      all: [...site.supportedLocales],
    };
  }

  return {
    defaultLocale: enabled.includes(site.defaultLocale) ? site.defaultLocale : enabled[0]!,
    enabled,
    all: [...site.supportedLocales],
  };
}
